import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Sparkles, ArrowUpDown } from 'lucide-react';
import { SORT_OPTIONS, formatCategoryLabel } from '../utils/sortUtils';
import { STATUS_OPTIONS, CATEGORY_DEFS, DEFAULT_EXPORT_CATEGORIES } from '../utils/exportFilterOptions';

export default function ExportImageOptionsModal({
  isOpen,
  onClose,
  onGenerate,
  protocolCount,
  onOptionsChange,
  currentSortOption = 'default'
}) {
  const [status, setStatus] = useState('Active');
  const [categories, setCategories] = useState(() => [...DEFAULT_EXPORT_CATEGORIES]);
  const [sortOption, setSortOption] = useState('default');
  const [obfuscateSupplier, setObfuscateSupplier] = useState(false);

  // Reset to defaults each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setStatus('Active');
      setCategories([...DEFAULT_EXPORT_CATEGORIES]);
      setSortOption(currentSortOption || 'default');
      setObfuscateSupplier(false);
    }
  }, [isOpen, currentSortOption]);

  // Notify parent of option changes for live count updates
  useEffect(() => {
    if (isOpen && typeof onOptionsChange === 'function') {
      onOptionsChange({
        status,
        categories,
        sortOption,
        obfuscateSupplier,
        category: formatCategoryLabel(categories)
      });
    }
  }, [status, categories, sortOption, obfuscateSupplier, isOpen, onOptionsChange]);

  if (!isOpen) return null;

  const toggleCategory = (catId) => {
    setCategories((prev) => {
      if (prev.includes(catId)) {
        return prev.filter((c) => c !== catId);
      } else {
        return [...prev, catId];
      }
    });
  };

  const selectAllCategories = () => {
    setCategories([...DEFAULT_EXPORT_CATEGORIES]);
  };

  const isAllSelected = categories.length === DEFAULT_EXPORT_CATEGORIES.length;

  const handleGenerate = () => {
    onGenerate({
      status,
      categories,
      sortOption,
      obfuscateSupplier,
      category: formatCategoryLabel(categories)
    });
  };

  const showCount = typeof protocolCount === 'number';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn overflow-y-auto touch-pan-y">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl relative flex flex-col max-h-[90vh] overflow-y-auto touch-pan-y">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center space-x-3 mb-5">
          <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <ImageIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Export Stack to Image</h2>
            <p className="text-xs text-slate-400">
              Choose status, multiple categories, and sort order for your image card
            </p>
          </div>
        </div>

        {/* Export Options */}
        <div className="space-y-4 text-xs">
          
          {/* Status Selection */}
          <div>
            <span className="block font-semibold text-slate-300 mb-1.5">Status Filter</span>
            <div className="flex rounded-xl bg-slate-950 p-1 border border-slate-800 gap-1">
              {STATUS_OPTIONS.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatus(st)}
                  className={`flex-1 py-1.5 px-2 font-semibold rounded-lg transition text-center ${
                    status === st ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          {/* Multi-Category Selection */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-semibold text-slate-300">
                Categories to Export <span className="text-[11px] font-normal text-slate-400">(Select multiple)</span>
              </span>
              <button
                type="button"
                onClick={selectAllCategories}
                className={`text-[11px] px-2 py-0.5 rounded transition border ${
                  isAllSelected
                    ? 'bg-slate-800 text-purple-400 border-purple-500/40 font-semibold'
                    : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                }`}
              >
                All Compounds
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {CATEGORY_DEFS.map(({ id, label, icon: Icon, color }) => {
                const isSelected = categories.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleCategory(id)}
                    className={`p-2.5 rounded-xl border flex flex-col items-center justify-center space-y-1 transition text-center ${
                      isSelected
                        ? color === 'sky'
                          ? 'bg-sky-500/15 border-sky-500/50 text-sky-300 shadow-sm'
                          : color === 'purple'
                          ? 'bg-purple-500/15 border-purple-500/50 text-purple-300 shadow-sm'
                          : 'bg-rose-500/15 border-rose-500/50 text-rose-300 shadow-sm'
                        : 'bg-slate-950/60 border-slate-800 text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[11px] font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sort Order Selector */}
          <div>
            <span className="block font-semibold text-slate-300 mb-1.5 flex items-center space-x-1">
              <ArrowUpDown className="w-3.5 h-3.5 text-purple-400" />
              <span>Sorting Order</span>
            </span>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs focus:outline-none focus:border-purple-500 transition cursor-pointer"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Obfuscate Supplier Option */}
          <div>
            <label className="flex items-start space-x-3 p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-slate-700 transition cursor-pointer select-none">
              <input
                type="checkbox"
                id="obfuscate-supplier-image"
                checked={obfuscateSupplier}
                onChange={(e) => setObfuscateSupplier(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-700 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900 bg-slate-900 cursor-pointer"
              />
              <div className="flex-1">
                <span className="font-semibold text-slate-200 block text-xs">
                  Obfuscate Supplier
                </span>
                <span className="text-[11px] text-slate-400 block mt-0.5">
                  Mask supplier in exported image with asterisks (e.g., NEXAPH → N*****)
                </span>
              </div>
            </label>
          </div>

        </div>

        {/* Record Count */}
        {showCount && (
          <p className="mt-4 text-[11px] text-slate-400 flex items-center justify-between bg-slate-950 p-2.5 rounded-xl border border-slate-800">
            <span className="flex items-center space-x-1.5">
              <ImageIcon className="w-3.5 h-3.5 text-purple-400" />
              <span>
                {protocolCount} {protocolCount === 1 ? 'record' : 'records'} included
              </span>
            </span>
            <span className="text-slate-500 font-mono text-[10px]">
              {formatCategoryLabel(categories)}
            </span>
          </p>
        )}

        {/* Footer Actions */}
        <div className="pt-4 border-t border-slate-800 mt-5 flex justify-end space-x-2.5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={protocolCount === 0}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs shadow-lg shadow-purple-500/20 transition flex items-center space-x-2"
          >
            <Sparkles className="w-4 h-4" />
            <span>Generate Image</span>
          </button>
        </div>

      </div>
    </div>
  );
}
